import { useEffect, useMemo, useState } from 'react';
import {
  PlusOutlined,
  BookTwoTone,
  BulbTwoTone,
  CloudTwoTone,
  FolderOpenTwoTone,
} from '@ant-design/icons';
import { Button, Form, Input, Menu, Modal, Typography, MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useNotificationContext } from '@/features/notifications';
import {
  WorkspaceResponse,
  useGetWorkspaces,
  useWorkspaceCreateForOrganization,
} from '@/shared/api';
import { useRouterContext, useRouterPrivateContext } from '@/shared/router';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { Col } from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';

const navigationKeys = ['knowledgeBase', 'myInstances', 'discover'] as const;
type NavigationKey = (typeof navigationKeys)[number];

type NavigationItem = {
  key: NavigationKey;
};

type WorkspaceItem = WorkspaceResponse & {
  key: string;
};

type MenuItemType = NavigationItem | WorkspaceItem;

const isNavigationItem = (item: MenuItemType): item is NavigationItem => {
  return Boolean(
    item.key && navigationKeys.some((navKey) => navKey === item.key),
  );
};

const isWorkspaceItem = (item: MenuItemType): item is WorkspaceItem => {
  return 'id' in item;
};

const { Title } = Typography;

export const SidebarLeft = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationContext();
  const { activeOrganization } = useRouterContext();
  const {
    selectedWorkspace,
    selectedCategory,
    setSelectedWorkspace,
    setSelectedCategory,
  } = useRouterPrivateContext();
  const { theme } = useThemeContext();

  const [createWorkspaceModalOpen, setCreateWorkspaceModalOpen] =
    useState(false);
  const { data: workspacesData, refetch: refetchWorkspaces } = useGetWorkspaces<
    WorkspaceResponse[]
  >(
    {},
    {
      onSuccess(data) {
        if (selectedWorkspace) {
          const updatedWorkspace = data.find(
            ({ id }) => id === selectedWorkspace.id,
          );
          setSelectedWorkspace(updatedWorkspace);
        }
      },
      select: ({ data }) => data,
    },
  );
  const { mutate: createWorkspace, isLoading: createWorkspaceLoading } =
    useWorkspaceCreateForOrganization();

  const items: MenuProps['items'] = useMemo(() => {
    const workspaces =
      workspacesData?.map((workspace: WorkspaceResponse) => ({
        label: workspace.title,
        key: workspace.id,
        onClick: () => onClick({ key: workspace.id, ...workspace }),
        style: {
          fontSize: 13,
        },
      })) || [];

    return [
      {
        label: 'Workspaces',
        key: 'workspaces_group',
        children: workspaces,
        icon: <FolderOpenTwoTone />,
        type: 'group',
      },
      { type: 'divider' },
      {
        label: 'Knowledge Base',
        key: 'knowledgeBase',
        icon: <BookTwoTone />,
        onClick: () => onClick({ key: 'knowledgeBase' }),
      },
      { type: 'divider' },
      {
        label: 'My Instances',
        key: 'myInstances',
        icon: <CloudTwoTone />,
        onClick: () => onClick({ key: 'myInstances' }),
      },
    ];
  }, [workspacesData]);

  const onClick = (item: MenuItemType) => {
    if (isNavigationItem(item)) {
      setSelectedWorkspace(undefined);
      setSelectedCategory(item.key);
      switch (item.key) {
        case 'knowledgeBase':
          navigate('/dashboard/knowledge-base');
          break;
        case 'myInstances':
          navigate('/dashboard/my-instances');
          break;
        case 'discover':
          navigate('/dashboard/discover');
          break;
      }
    } else if (isWorkspaceItem(item)) {
      setSelectedWorkspace(item);
      setSelectedCategory('myProcs');
      navigate(`/dashboard/workspaces/${item.id}`);
    }
  };

  const handleWorkspaceCreate = async (data: any) => {
    try {
      createWorkspace(
        {
          pathParams: {
            organizationId: activeOrganization?.id!,
          },
          body: {
            title: data.workspaceName,
          },
        },
        {
          onSuccess: (data: any) => {
            refetchWorkspaces();
            setSelectedWorkspace(data);
            setCreateWorkspaceModalOpen(false);
            showNotification({
              type: 'success',
              message: 'Workspace created',
              description: `Workspace ${data.title} has been created successfully`,
            });
          },
          onError: (error: any) => {
            if (error.stack.message) {
              showNotification({
                type: 'error',
                message: 'Workspace creation failed',
                description: error.stack.message,
              });
            }
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'sidebar_left',
        action: 'create_workspace',
      });
    }
  };

  useEffect(() => {
    if (!workspacesData || workspacesData.length === 0) {
      return;
    }
    if (
      location.pathname === '/dashboard' ||
      location.pathname === '/dashboard/'
    ) {
      setSelectedWorkspace(workspacesData[0]);
    }
    if (
      location.pathname.includes('/dashboard/workspaces/') &&
      !selectedWorkspace?.id
    ) {
      setSelectedWorkspace(
        workspacesData.find(
          (w: any) =>
            w.id ===
            location.pathname.substring(location.pathname.lastIndexOf('/') + 1),
        ),
      );
    }
  }, [workspacesData]);

  useEffect(() => {
    if (selectedWorkspace?.id) {
      navigate(`/dashboard/workspaces/${selectedWorkspace?.id}`);
    }
  }, [selectedWorkspace?.id]);

  if (!workspacesData) {
    return null;
  }

  return (
    <>
      <SidebarWrapper>
        <SidebarLeftContent
          $color={themeConfig[theme].colors.activeItemPrimaryColor}
        >
          {activeOrganization?.role === 'admin' && (
            <Button
              data-testid="create-workspace-button"
              icon={<PlusOutlined />}
              size="small"
              disabled={createWorkspaceLoading}
              onClick={() => setCreateWorkspaceModalOpen(true)}
              style={{ position: 'absolute', top: 21, right: 5 }}
            />
          )}
          <Menu
            style={{ width: 200 }}
            defaultSelectedKeys={[workspacesData[0]?.id]}
            selectedKeys={
              selectedCategory === 'myProcs'
                ? [selectedWorkspace?.id || workspacesData[0]?.id]
                : [selectedCategory]
            }
            mode="inline"
            items={items}
          />
        </SidebarLeftContent>
      </SidebarWrapper>
      <Modal
        data-testid="create-workspace-modal"
        title={
          <Col
            alignItems="flex-start"
            style={{ margin: '10px 0', width: '100%' }}
          >
            <Title level={4} style={{ lineHeight: 1, marginTop: 15 }}>
              Create a new workspace
            </Title>
          </Col>
        }
        destroyOnClose
        width={350}
        open={createWorkspaceModalOpen}
        footer={null}
        style={{ maxWidth: '90%' }}
        onCancel={() => setCreateWorkspaceModalOpen(false)}
      >
        <Form
          name="createWorkspaceForm"
          initialValues={{
            remember: true,
          }}
          onFinish={handleWorkspaceCreate}
          layout="vertical"
        >
          <Form.Item
            name="workspaceName"
            label="Workspace name"
            rules={[
              {
                required: true,
                message: 'Workspace name is required',
              },
            ]}
            style={{ marginTop: 20 }}
          >
            <Input />
          </Form.Item>
          <Form.Item style={{ width: '100%', marginTop: 40 }}>
            <Button
              type="primary"
              htmlType="submit"
              style={{ width: '100%', height: 40 }}
            >
              Create workspace
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const SidebarWrapper = styled.div`
  max-height: calc(100vh - 50px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  position: relative;
  overflow-y: hidden;
`;

const SidebarLeftContent = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  .ant-menu-light .ant-menu-item-selected {
    color: ${({ $color }) => $color};
  }
  overflow-y: auto;
  padding-bottom: 20px;
`;
